import { memo, useState } from 'react';

import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { Image } from '@realadvisor/image';
import {
  type RentalComparable,
  netRentPerSqm,
} from '@realadvisor/listings/rental-cma';
import { Building2Icon, MapPinIcon, UserIcon } from 'lucide-react';

import { NoPhotosPlaceholder } from '@modules/_shared/components/NoPhotoPlaceholder';
import { usePropertyTypes } from '@modules/_shared/providers/PropertyTypesProvider';
import {
  formatAddress,
  formatDistance,
} from '@modules/_shared/utils/formatting';
import type { ListingItem } from '@modules/_shared/utils/listingDataTransformers';
import { getPropertyCharacteristics } from '@modules/_shared/utils/listingUtils';
import ListingStatusChip from '@modules/reports/cma-reports/step-listing-compare/ListingStatusChip';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

export type ScraperListingCardProps = {
  listing: Pick<ListingItem, 'property_type'> &
    Partial<
      Pick<
        ListingItem,
        | 'images'
        | 'visit_contact_person'
        | 'agency_contact_person'
        | 'agency_name'
        | 'number_of_rooms'
        | 'number_of_bedrooms'
        | 'computed_surface'
        | 'land_surface'
        | 'route'
        | 'street_number'
        | 'postcode'
        | 'locality'
        | 'state'
        | 'sale_price'
        | 'computed_price_per_sqm'
        | 'valid_from'
        | 'offline_since'
      >
    >;
  rentalPrice?: Pick<
    RentalComparable,
    'netMonthly' | 'chargesMonthly' | 'surface'
  >;
  addressLabel?: string;
  variant: 'vertical' | 'horizontal';

  onClick?: () => void;
  onHover?: () => void;
  action?: React.ReactNode;

  showAgencyName?: boolean;
  showContactName?: boolean;
  showDistance?: boolean;
  distanceInMeters?: number | null;
  showPricePerSqm?: boolean;
  showDaysOnMarket?: boolean;
  showOfflineSince?: boolean;
  dateBadgePosition?: 'image' | 'inline';
  compact?: boolean;
  imageLoading?: 'lazy';
};

const ScraperListingCard = ({
  listing,
  rentalPrice,
  addressLabel,
  variant,
  onClick,
  onHover,
  action,
  showAgencyName = false,
  showContactName = false,
  showDistance = false,
  distanceInMeters,
  showPricePerSqm = false,
  showDaysOnMarket = false,
  showOfflineSince = false,
  dateBadgePosition = 'image',
  compact = false,
  imageLoading,
}: ScraperListingCardProps) => {
  const { getPropertyTypeLabel } = usePropertyTypes();
  const { t, locale, countryCode } = useLocale();
  const theme = useTheme();
  const [imageLoadError, setImageLoadError] = useState(false);

  const [primaryImage] = listing.images ?? [];
  const currencyToFormat = getCurrencyByCountryCode(countryCode);
  const price = rentalPrice?.netMonthly ?? listing.sale_price;
  const pricePerSqm = rentalPrice
    ? netRentPerSqm(rentalPrice)
    : listing.computed_price_per_sqm;

  const agentName =
    listing.visit_contact_person ?? listing.agency_contact_person;

  const showDateBadgesOnImage =
    dateBadgePosition === 'image' && (showDaysOnMarket || showOfflineSince);
  const showDateBadgesInline =
    dateBadgePosition === 'inline' && (showDaysOnMarket || showOfflineSince);

  return (
    <Stack
      data-testid="scraper-listing-card"
      direction={variant === 'vertical' ? 'column' : 'row'}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box
        onMouseEnter={onHover}
        onClick={onClick}
        sx={[
          {
            background: (theme.vars ?? theme).palette.grey[200],
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            justifyContent: 'center',
          },
          variant === 'vertical'
            ? { height: compact ? '150px' : '180px', alignSelf: 'stretch' }
            : {
                width: { xs: '124px', sm: '160px' },
                minWidth: { xs: '124px', sm: '160px' },
              },
        ]}
      >
        {primaryImage != null && !imageLoadError ? (
          <Box sx={{ flexGrow: 1, height: 0 }}>
            <Image
              src={{
                bucketName: primaryImage.bucket_name ?? '',
                fileName: primaryImage.file_name ?? '',
              }}
              loading={imageLoading}
              onError={() => setImageLoadError(true)}
            />
          </Box>
        ) : (
          <NoPhotosPlaceholder ratio={variant === 'vertical' ? 16 / 9 : 1} />
        )}

        {showDateBadgesOnImage && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 1,
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 1,
            }}
          >
            {showDaysOnMarket && (
              <ListingStatusChip
                listing={listing}
                variant="duration"
                shortLabels
                compact={compact}
              />
            )}
            {showOfflineSince && (
              <ListingStatusChip
                listing={listing}
                variant="status"
                shortLabels
                compact={compact}
              />
            )}
          </Box>
        )}

        {showAgencyName && listing.agency_name && variant === 'vertical' && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              right: 8,
            }}
          >
            <Chip
              icon={<Building2Icon size={compact ? 14 : 18} />}
              label={listing.agency_name}
              variant="outlined"
              size="small"
              sx={{
                px: 0.5,
                ...(compact ? { height: 24, fontSize: '12px' } : { py: 1.5 }),
                backgroundColor: 'background.paper',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Box>
        )}
      </Box>
      <Stack
        onClick={onClick}
        sx={{
          px: variant === 'vertical' ? 1.5 : 2,
          py: variant === 'vertical' ? 1.5 : 1,
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
        }}
      >
        <Typography
          sx={{
            lineHeight: 1.3,
            fontWeight: compact ? 700 : 'bold',
            fontSize: compact ? '14px' : '1rem',
            whiteSpace: 'nowrap',
          }}
        >
          {getPropertyTypeLabel(listing.property_type)}
        </Typography>

        <Typography
          component="div"
          sx={{
            ...theme.typography.body2,
            color: (theme.vars ?? theme).palette.grey['800'],
            fontSize: '0.8rem',
          }}
        >
          <Box>{getPropertyCharacteristics(listing, countryCode, t)}</Box>
          <Box component="span" sx={{ marginRight: 0.5 }}>
            {addressLabel ??
              formatAddress(
                {
                  ...listing,
                  state: listing.state?.name,
                },
                ', ',
              )}
          </Box>
          {showDistance && distanceInMeters != null && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
              }}
            >
              •
              <MapPinIcon
                size={compact ? 12 : 14}
                color={'var(--mui-palette-text-secondary)'}
              />
              {formatDistance(distanceInMeters)}
            </Box>
          )}
        </Typography>

        <Box
          sx={{
            flexGrow: 1,
          }}
        />

        <Box
          sx={{
            mt: 1,
          }}
        >
          {rentalPrice && (
            <Typography variant="caption" color="text.secondary">
              {t('Monthly net rent')}
            </Typography>
          )}
          <Typography
            variant={compact ? 'body1' : 'h6'}
            sx={{
              ...(compact ? { fontSize: '16px' } : {}),
              fontWeight: compact ? 700 : 'bold',
              lineHeight: 1.3,
            }}
          >
            {price
              ? formatPrice(price, locale, currencyToFormat)
              : t('priceOnRequest')}
            {rentalPrice && ` ${t('per month short')}`}
          </Typography>
          {showPricePerSqm && (
            <Typography
              sx={{
                ...theme.typography.body2,
                color: (theme.vars ?? theme).palette.grey['800'],
              }}
            >
              {pricePerSqm
                ? formatPrice(pricePerSqm, locale, currencyToFormat)
                : '- '}
              / m²
              {rentalPrice && ` ${t('per month short')}`}
            </Typography>
          )}
          {rentalPrice?.chargesMonthly != null &&
            rentalPrice.chargesMonthly > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('Monthly charges')}:{' '}
                {formatPrice(
                  rentalPrice.chargesMonthly,
                  locale,
                  currencyToFormat,
                )}
              </Typography>
            )}
        </Box>

        {(showContactName || (showAgencyName && variant === 'horizontal')) && (
          <Stack
            direction="row"
            sx={{
              gap: 1,
              flexWrap: 'wrap',
              mt: 1,
            }}
          >
            {showContactName && agentName && (
              <Chip
                icon={<UserIcon size={compact ? 14 : 18} />}
                label={agentName}
                size={compact ? 'small' : undefined}
                sx={compact ? { height: 24, fontSize: '12px' } : undefined}
              />
            )}
            {showAgencyName &&
              listing.agency_name &&
              variant === 'horizontal' && (
                <Chip
                  icon={<Building2Icon size={compact ? 14 : 18} />}
                  label={listing.agency_name}
                  variant="outlined"
                  size={compact ? 'small' : undefined}
                  sx={compact ? { height: 24, fontSize: '12px' } : undefined}
                />
              )}
          </Stack>
        )}

        {showDateBadgesInline && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'column',
              gap: 1,
              mt: 0.5,
            }}
          >
            {showDaysOnMarket && (
              <ListingStatusChip
                listing={listing}
                variant="duration"
                compact={compact}
              />
            )}
            {showOfflineSince && (
              <ListingStatusChip
                listing={listing}
                variant="status"
                compact={compact}
              />
            )}
          </Box>
        )}

        {action && (
          <Box
            sx={{
              mt: 1,
            }}
          >
            {action}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default memo(ScraperListingCard);
